import { Dropdown, DropdownRef, Input, Menu, Typography } from '@recruit-robin/robin-theme/web-components';
import { ChevronDown } from '@recruit-robin/robin-theme/web-icons';
import { useEffect, useRef, useState } from 'react';

import { useDictionary } from '@/shared/lib/i18n';

import type { RangeValue, TimeUnit } from '../../model';

import { useRangeInputRequirementStyles } from './RangeInputRequirement.styles';

export type RangeInputRequirementProps = {
  value?: RangeValue;
  onChange: (value: RangeValue) => void;
  unit?: TimeUnit;
  onUnitChange?: (value: TimeUnit) => void;
  unitOptions?: { label: string; value: TimeUnit }[];
  isFixedUnit?: boolean;
  unitLabel?: string;
  toLabel?: string;
};

export const RangeInputRequirement = ({
  value,
  onChange,
  unit,
  onUnitChange,
  unitOptions,
  isFixedUnit,
  unitLabel,
  toLabel,
}: RangeInputRequirementProps) => {
  const classes = useRangeInputRequirementStyles();
  const dictionary = useDictionary();
  const unitDropdownRef = useRef<DropdownRef>(null);
  const currentValue = {
    min: value?.min ?? 0,
    max: value?.max ?? 0,
  };

  const formatValue = (val: number | undefined) => (val === 0 || val == null ? '' : String(val));

  const [minInputValue, setMinInputValue] = useState(() => formatValue(currentValue.min));
  const [maxInputValue, setMaxInputValue] = useState(() => formatValue(currentValue.max));

  useEffect(() => {
    if (!minInputValue) setMinInputValue(formatValue(currentValue.min));
  }, [currentValue.min, minInputValue]);

  useEffect(() => {
    if (!maxInputValue) setMaxInputValue(formatValue(currentValue.max));
  }, [currentValue.max, maxInputValue]);

  const parseNumericValue = (inputValue: string) => parseFloat(inputValue.replace(',', '.')) || 0;

  const handleMinChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    setMinInputValue(inputValue);
    onChange({ min: parseNumericValue(inputValue), max: currentValue.max });
  };

  const handleMaxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    setMaxInputValue(inputValue);
    onChange({ min: currentValue.min, max: parseNumericValue(inputValue) });
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const { key } = event;
    const currentInputValue = (event.target as HTMLInputElement).value;

    const isControlKey = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(key);
    const isNumber = /^[0-9]$/.test(key);
    const isDecimalSeparator = key === '.' || key === ',';
    const hasDecimalSeparator = currentInputValue.includes('.') || currentInputValue.includes(',');

    if (!isNumber && !isControlKey && !(isDecimalSeparator && !hasDecimalSeparator)) {
      event.preventDefault();
    }
  };

  return (
    <div css={classes.rangeInputContainer}>
      <Input
        type="text"
        inputMode="decimal"
        pattern="[0-9]*[.,]?[0-9]*"
        value={minInputValue}
        onChange={handleMinChange}
        onKeyPress={handleKeyPress}
        placeholder="Min"
        contained
        min={0}
      />
      <Typography variant={(v) => v.body.short} color={(c) => c.neutral.variant[60]}>
        {toLabel ?? dictionary.to}
      </Typography>
      <Input
        type="text"
        inputMode="decimal"
        pattern="[0-9]*[.,]?[0-9]*"
        value={maxInputValue}
        onChange={handleMaxChange}
        onKeyPress={handleKeyPress}
        placeholder="Max"
        contained
        min={0}
      />

      {onUnitChange != null && unit != null && unitOptions != null && (
        <Dropdown
          ref={unitDropdownRef}
          placement="bottom-end"
          flipMenu={false}
          shouldUsePortal={false}
          disabled={isFixedUnit}
          renderMenu={
            <Menu.Root css={classes.unitDropdownMenu}>
              {unitOptions.map((option) => (
                <Menu.Item.Root
                  key={option.value}
                  onClick={() => {
                    onUnitChange(option.value);
                    unitDropdownRef.current?.close();
                  }}>
                  {option.label}
                </Menu.Item.Root>
              ))}
            </Menu.Root>
          }>
          {() => (
            <div css={classes.dropdownButton}>
              <Typography variant={(v) => v.supporting.label} color={(c) => c.neutral.variant[70]}>
                {unitLabel ?? unit}
              </Typography>
              {!isFixedUnit && <ChevronDown size={16} />}
            </div>
          )}
        </Dropdown>
      )}
    </div>
  );
};
